import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { isUserPermitted } from 'app/cross-cutting-concerns/authentication/utils/isUserPermitted';
import { selectPermissions } from 'app/cross-cutting-concerns/authentication/state/authenticationSelectors';
import { selectIsEditMachineInfoModalVisible } from 'app/modules/machine-inventory/modals/state/machineModalsSelectors';
import { MachineModalsActions } from 'app/modules/machine-inventory/modals/state/machineModalsSlice';
import { Permission } from 'config/permissions';

export const EDIT_MACHINE_MODAL_QUERY_PARAM = 'editModal';

/**
 * Opens the edit machine info modal when the URL contains `?editModal=true`
 * and the user has machine update permission (admin / strategic manager).
 * Keeps the query param while the modal is open and removes it when the modal closes.
 */
export const useOpenEditMachineModalFromUrl = (): void => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const userPermissions = useSelector(selectPermissions);
  const isVisible = useSelector(selectIsEditMachineInfoModalVisible);
  const shouldOpenEditModal = searchParams.get(EDIT_MACHINE_MODAL_QUERY_PARAM) === 'true';
  const wasVisibleRef = useRef(false);
  const canEdit = isUserPermitted(userPermissions, [Permission.Machine.UPDATE]);

  useEffect(() => {
    if (!shouldOpenEditModal) {
      wasVisibleRef.current = isVisible;
      return;
    }

    if (!canEdit) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete(EDIT_MACHINE_MODAL_QUERY_PARAM);
      setSearchParams(nextParams, { replace: true });
      return;
    }

    const wasVisible = wasVisibleRef.current;

    if (wasVisible && !isVisible) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete(EDIT_MACHINE_MODAL_QUERY_PARAM);
      setSearchParams(nextParams, { replace: true });
      wasVisibleRef.current = false;
      return;
    }

    if (!isVisible && !wasVisible) {
      dispatch(MachineModalsActions.showEditMachineInfoModal());
    }

    wasVisibleRef.current = isVisible;
  }, [canEdit, dispatch, isVisible, searchParams, setSearchParams, shouldOpenEditModal]);
};
